import { isNonEmptyString } from '@/lib/utils/type-guards';

/**
 * Canonical helpers for profile URLs.
 *
 * The database persists the raw Twitter `url` field (typically t.co).
 * These helpers expand the stored value to its destination for presentation
 * using the normalized entities payload we stash alongside the profile.
 */

type NormalizedUrlEntity = {
  url: string;
  expandedUrl: string;
  displayUrl?: string;
  start?: number;
  end?: number;
  unwoundUrl?: string;
};

type NormalizedUrlBlock = { urls?: NormalizedUrlEntity[] };

type NormalizedDescriptionBlock = {
  urls?: NormalizedUrlEntity[];
  mentions?: Array<{ username?: string; start?: number; end?: number }>;
  hashtags?: Array<{ tag?: string; start?: number; end?: number }>;
};

type LegacyUrlEntityFields = {
  url?: string | null;
  expandedUrl?: string | null;
  expanded_url?: string | null;
  displayUrl?: string | null;
  display_url?: string | null;
  unwoundUrl?: string | null;
  unwound_url?: string | null;
  title?: string | null;
  description?: string | null;
  start?: number | null;
  end?: number | null;
};

type UrlEntityCandidate = (NormalizedUrlEntity & LegacyUrlEntityFields) | LegacyUrlEntityFields | null | undefined;

type UrlBlockCandidate =
  | {
      urls?: UrlEntityCandidate[] | null;
    }
  | null
  | undefined;

type DescriptionMentionRecord = {
  username?: string | null;
  start?: number | null;
  end?: number | null;
};

type DescriptionMentionCandidate = DescriptionMentionRecord | null | undefined;

type DescriptionHashtagRecord = {
  tag?: string | null;
  start?: number | null;
  end?: number | null;
};

type DescriptionHashtagCandidate = DescriptionHashtagRecord | null | undefined;

type DescriptionBlockCandidate =
  | {
      urls?: UrlEntityCandidate[] | null;
      mentions?: DescriptionMentionCandidate[] | null;
      hashtags?: DescriptionHashtagCandidate[] | null;
    }
  | null
  | undefined;

type NormalizedEntitiesCandidate =
  | NormalizedUserEntities
  | {
      url?: UrlBlockCandidate;
      description?: DescriptionBlockCandidate;
    }
  | string
  | null
  | undefined;

type UrlEntityRecord = Exclude<UrlEntityCandidate, null | undefined>;
type UrlBlockRecord = Exclude<UrlBlockCandidate, null | undefined>;
type DescriptionBlockRecord = Exclude<DescriptionBlockCandidate, null | undefined>;
type EntitiesRecord = Exclude<NormalizedEntitiesCandidate, string | null | undefined>;

const isUrlEntityRecord = (candidate: UrlEntityCandidate): candidate is UrlEntityRecord =>
  Boolean(candidate && typeof candidate === 'object');

const isUrlBlockRecord = (candidate: UrlBlockCandidate): candidate is UrlBlockRecord =>
  Boolean(candidate && typeof candidate === 'object');

const isDescriptionBlockRecord = (candidate: DescriptionBlockCandidate): candidate is DescriptionBlockRecord =>
  Boolean(candidate && typeof candidate === 'object');

const isEntitiesRecord = (candidate: NormalizedEntitiesCandidate): candidate is EntitiesRecord =>
  Boolean(candidate && typeof candidate === 'object');

export type NormalizedUserEntities =
  | {
      url?: NormalizedUrlBlock;
      description?: NormalizedDescriptionBlock;
    }
  | null
  | undefined;

function isNormalizedUrlEntityList(value: UrlEntityCandidate[] | null | undefined): value is NormalizedUrlEntity[] {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every((entry) => {
    if (!isUrlEntityRecord(entry)) {
      return false;
    }
    return isNonEmptyString(entry.url) && isNonEmptyString(entry.expandedUrl) && !('expanded_url' in entry);
  });
}

function isNormalizedEntities(value: NormalizedEntitiesCandidate): value is NormalizedUserEntities {
  if (!isEntitiesRecord(value)) return false;

  if (
    value.url &&
    typeof value.url === 'object' &&
    value.url.urls !== undefined &&
    !isNormalizedUrlEntityList(value.url.urls)
  ) {
    return false;
  }

  if (
    value.description &&
    typeof value.description === 'object' &&
    value.description.urls !== undefined &&
    !isNormalizedUrlEntityList(value.description.urls)
  ) {
    return false;
  }

  return true;
}

function toNormalizedUrlEntity(candidate: UrlEntityCandidate): NormalizedUrlEntity | null {
  if (!isUrlEntityRecord(candidate)) {
    return null;
  }

  const record = candidate;
  const primaryUrl = isNonEmptyString(record.url) ? record.url : undefined;
  const fallbackUrl = (() => {
    if (isNonEmptyString(record.expandedUrl)) return record.expandedUrl;
    if (isNonEmptyString(record.expanded_url)) return record.expanded_url;
    if (isNonEmptyString(record.unwoundUrl)) return record.unwoundUrl;
    if (isNonEmptyString(record.unwound_url)) return record.unwound_url;
    return;
  })();

  const canonicalUrl = primaryUrl ?? fallbackUrl;
  if (!canonicalUrl) {
    return null;
  }

  const expandedUrl = (() => {
    if (isNonEmptyString(record.expandedUrl)) return record.expandedUrl;
    if (isNonEmptyString(record.expanded_url)) return record.expanded_url;
    if (isNonEmptyString(record.unwoundUrl)) return record.unwoundUrl;
    if (isNonEmptyString(record.unwound_url)) return record.unwound_url;
    return canonicalUrl;
  })();

  const displayUrl = (() => {
    if (isNonEmptyString(record.displayUrl)) return record.displayUrl;
    if (isNonEmptyString(record.display_url)) return record.display_url;
    return;
  })();

  const normalized: NormalizedUrlEntity = {
    url: canonicalUrl,
    expandedUrl,
  };

  if (displayUrl) {
    normalized.displayUrl = displayUrl;
  }

  let unwound: string | undefined;
  if (isNonEmptyString(record.unwoundUrl)) {
    unwound = record.unwoundUrl;
  } else if (isNonEmptyString(record.unwound_url)) {
    unwound = record.unwound_url;
  }
  if (unwound) {
    normalized.unwoundUrl = unwound;
  }

  if (typeof record.start === 'number') {
    normalized.start = record.start;
  }
  if (typeof record.end === 'number') {
    normalized.end = record.end;
  }

  return normalized;
}

function normalizeUrlBlock(value: UrlBlockCandidate): NormalizedUrlBlock | undefined {
  if (!isUrlBlockRecord(value)) {
    return;
  }
  const urlsInput = value.urls;
  if (!Array.isArray(urlsInput)) {
    return;
  }
  const urls = urlsInput
    .map((entry) => toNormalizedUrlEntity(entry))
    .filter((entry): entry is NormalizedUrlEntity => entry !== null);
  if (urls.length === 0) {
    return;
  }
  return { urls };
}

function normalizeDescriptionBlock(value: DescriptionBlockCandidate): NormalizedDescriptionBlock | undefined {
  if (!isDescriptionBlockRecord(value)) {
    return;
  }

  const description = value;
  const normalized: NormalizedDescriptionBlock = {};

  const urls = normalizeUrlBlock(description);
  if (urls?.urls && urls.urls.length > 0) {
    normalized.urls = urls.urls;
  }

  if (Array.isArray(description.mentions)) {
    const mentions = description.mentions
      .map((mention) => {
        if (!mention) {
          return null;
        }
        const username = isNonEmptyString(mention.username) ? mention.username : undefined;
        const start = typeof mention.start === 'number' ? mention.start : undefined;
        const end = typeof mention.end === 'number' ? mention.end : undefined;
        return username
          ? { username, ...(start === undefined ? {} : { start }), ...(end === undefined ? {} : { end }) }
          : null;
      })
      .filter((mention): mention is { username: string; start?: number; end?: number } => mention !== null);
    if (mentions.length > 0) {
      normalized.mentions = mentions;
    }
  }

  if (Array.isArray(description.hashtags)) {
    const hashtags = description.hashtags
      .map((hashtag) => {
        if (!hashtag) {
          return null;
        }
        const tag = isNonEmptyString(hashtag.tag) ? hashtag.tag : undefined;
        const start = typeof hashtag.start === 'number' ? hashtag.start : undefined;
        const end = typeof hashtag.end === 'number' ? hashtag.end : undefined;
        return tag ? { tag, ...(start === undefined ? {} : { start }), ...(end === undefined ? {} : { end }) } : null;
      })
      .filter((hashtag): hashtag is { tag: string; start?: number; end?: number } => hashtag !== null);
    if (hashtags.length > 0) {
      normalized.hashtags = hashtags;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function normalizeEntities(value: NormalizedEntitiesCandidate): NormalizedUserEntities {
  if (!isEntitiesRecord(value)) {
    return;
  }

  const record = value;
  const normalized: { url?: NormalizedUrlBlock; description?: NormalizedDescriptionBlock } = {};

  const urlBlock = normalizeUrlBlock(record.url);
  if (urlBlock) {
    normalized.url = urlBlock;
  }

  const descriptionBlock = normalizeDescriptionBlock(record.description);
  if (descriptionBlock) {
    normalized.description = descriptionBlock;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Expand a profile URL when entities provide a canonical destination.
 * Falls back to the original value if no mapping is available.
 */
export function expandProfileUrl(
  url: string | null | undefined,
  entities: NormalizedEntitiesCandidate
): string | null | undefined {
  if (!url) {
    return url ?? null;
  }

  const normalized = ensureProfileEntities(entities);
  if (!normalized) {
    return url;
  }

  const urlEntities = normalized.url?.urls;
  if (!urlEntities || urlEntities.length === 0) {
    return url;
  }

  for (const candidate of urlEntities) {
    if (candidate?.url === url && typeof candidate.expandedUrl === 'string' && candidate.expandedUrl.length > 0) {
      return candidate.expandedUrl;
    }
  }

  if (isTwitterShortLink(url) && urlEntities.length === 1) {
    const [candidate] = urlEntities;
    if (candidate && typeof candidate.expandedUrl === 'string' && candidate.expandedUrl.length > 0) {
      return candidate.expandedUrl;
    }
  }

  return url;
}

function isTwitterShortLink(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === 't.co';
  } catch {
    return false;
  }
}

export function ensureProfileEntities(value: unknown): NormalizedUserEntities | undefined {
  if (value === undefined) {
    return;
  }

  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return ensureProfileEntities(parsed as NormalizedEntitiesCandidate);
    } catch {
      return;
    }
  }

  const normalized = normalizeEntities(value);
  if (normalized) {
    return normalized;
  }

  if (isNormalizedEntities(value)) {
    return value;
  }

  return;
}
